import React, { createContext, useContext, useState, useEffect } from 'react';
import {useAuth} from "./AuthContext.jsx";
import { useLocation } from 'react-router-dom'; // Add this import

const OnboardingContext = createContext();

export const useOnboarding = () => {
    const context = useContext(OnboardingContext);
    if (!context) {
        throw new Error('useOnboarding must be used within OnboardingProvider');
    }
    return context;
};

export const OnboardingProvider = ({ children }) => {
    const { user, token } = useAuth(); // Get auth state
    const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);
    const [showTutorial, setShowTutorial] = useState(false);
    const [onboardingStep, setOnboardingStep] = useState(0);
    const [loading, setLoading] = useState(true);

    // Check onboarding status on mount
    useEffect(() => {
        if (user && token) {
            checkOnboardingStatus();
        } else {
            setLoading(false);
        }
    }, [user, token]); // Depend on auth state


    useEffect(() => {
        // console.log('OnboardingProvider state changed:', {
        //     showTutorial,
        //     hasCompletedOnboarding,
        //     onboardingStep
        // });
    }, [showTutorial, hasCompletedOnboarding, onboardingStep]);

    const checkOnboardingStatus = async () => {
        if (!token) {
            setLoading(false);
            return;
        }

        try {
            const response = await fetch('/api/user/onboarding-status', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setHasCompletedOnboarding(data.completed || false);
            }
        } catch (error) {
            console.error('Failed to check onboarding status:', error);
        } finally {
            setLoading(false);
        }
    };

    const markOnboardingComplete = async () => {
        try {
            await fetch('/api/user/onboarding-complete', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                }
            });

            setHasCompletedOnboarding(true);
            setShowTutorial(false);
        } catch (error) {
            console.error('Failed to mark onboarding complete:', error);
        }
    };

    const startTutorial = async () => {
        console.log('startTutorial called - checking progress first');

        try {
            // Check current setup status first
            const response = await fetch('/api/user/setup-status', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                const setupData = data.data;

                // Determine starting step based on progress
                let startingStep = 0;
                if (setupData.openaiConfigured && !setupData.platformsConfigured) {
                    startingStep = 3; // Social platforms step
                } else if (setupData.openaiConfigured) {
                    startingStep = 4; // Tutorial overview
                } else {
                    startingStep = 1; // OpenAI setup
                }

                setOnboardingStep(startingStep);
                console.log('Starting tutorial at step:', startingStep);
            } else {
                setOnboardingStep(0); // Default to beginning
            }
        } catch (error) {
            console.error('Failed to check progress, starting from beginning:', error);
            setOnboardingStep(0);
        }

        setShowTutorial(true);
    };

    const resetOnboarding = async () => {
        try {
            await fetch('/api/user/onboarding-reset', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
                }
            });

            setHasCompletedOnboarding(false);
            setShowTutorial(false);
            setOnboardingStep(0);
        } catch (error) {
            console.error('Failed to reset onboarding:', error);
        }
    };

    return (
        <OnboardingContext.Provider value={{
            hasCompletedOnboarding,
            showTutorial,
            onboardingStep,
            loading,
            setShowTutorial,
            setOnboardingStep,
            startTutorial,
            markOnboardingComplete,
            resetOnboarding
        }}>
            {children}
        </OnboardingContext.Provider>
    );
};